import {getTestProps} from '@benchling/js-test-utils';
import {noop} from '@benchling/js-utils';
import {Switch} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import FolderSelector from 'file-browser/FolderSelector';
import {
  getFragmentBinSwatchStyles,
  OutputSection,
} from 'molbio/bulk-assembly/assemble-modal/PerformAssemblyDefineOutputsPane';
import {
  PerformAssemblyModalState,
  PerformAssemblyModalAction,
  ENTITY_TYPE_TO_LOCATION_KEY,
} from 'molbio/bulk-assembly/assemble-modal/performAssemblyModalReducer';
import {BinDetails} from 'molbio/bulk-assembly/BulkAssemblyConstants';
import {getConstructsSwatchesStyles} from 'molbio/bulk-assembly/card/BulkAssemblyCard';
import {WorklistInfo} from 'molbio/components/SaveLocationPicker';
import EntrySearchInput from 'notebook/entries/EntrySearchInput';
import {
  EntryTableDropdownWithCreation,
  TableSelection,
  TableSelectionType,
} from 'notebook/structured-tables/components/EntryTableDropdown';
import {StructuredTableKind} from 'notebook/structured-tables/StructuredTableConstants';
import {getMaxRowsConfig} from 'notebook/structured-tables/util/checkTableCapacity';
import TagSchemaSelector from 'schemas/shared/TagSchemaSelector';
import {SearchContext} from 'search/SearchConstants';
import StudySearchInput from 'studies/StudySearchInput';
import {
  FONT_PROXIMA_13,
  FONT_WEIGHT_PROXIMA_REGULAR,
  FONT_PROXIMA_14,
  FONT_WEIGHT_PROXIMA_SEMIBOLD,
  FONT_PROXIMA_12,
} from 'styles/FontConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {ButtonSize} from 'ui-library/buttons/Button';
import * as Colors from 'ui-library/Colors';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {FolderItemIcon} from 'ui-library/icons/IconConstants';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {isEnterKey} from 'util/Keyboard';
import {ElementKeyboardEvent} from 'util/Types';
import {WorklistType} from 'worklists/WorklistConstants';
import {WorklistSearchInputWithCreation} from 'worklists/WorklistSearchInputWithCreation';
/**
 * Custom logic to handle Enter key events in text inputs without submitting the modal
 */
function blurOnEnter(e: ElementKeyboardEvent): void {
  if (isEnterKey(e)) {
    e.preventDefault();
    (e.target as HTMLInputElement).blur();
  }
}

/**
 * Creates a schema dropdown section for an output with styling
 */
function SchemaDropdownSection({
  selectedSchemaId,
  disabled,
}: {
  selectedSchemaId: string | null;
  disabled: boolean;
}): JSX.Element {
  return (
    <div className={css(styles.dropdownSection)}>
      <div className={css(styles.dropdownTitle)}>Schema</div>
      <TagSchemaSelector
        selectedTagSchemaId={selectedSchemaId}
        isDisabled={disabled}
        onChange={noop}
        allowNone={true}
      />
    </div>
  );
}

/**
 * Creates an entry table dropdown section for an output with styling
 */
function EntryTableDropdownSection({
  documentLikeId,
  selectedTable,
  selectedSchemaId,
  entityCount,
  disabled,
  onTableSelect,
  onSelectCreateNewTable,
  onClearTableName,
}: {
  documentLikeId?: string;
  selectedTable: TableSelection;
  selectedSchemaId: string | null;
  disabled: boolean;
  entityCount: number;
  onTableSelect: (selection: TableSelection) => void;
  onSelectCreateNewTable: (tableName: string) => void;
  onClearTableName: () => void;
}): JSX.Element {
  const maxRegistrationTableRows = getMaxRowsConfig(true, StructuredTableKind.REGISTRATION);
  return (
    <div className={css(styles.dropdownSection)}>
      <div className={css(styles.dropdownTitle)}>
        Entry registration table
        {entityCount > maxRegistrationTableRows && (
          <Tooltip
            placement={FloatingPlacement.TOP}
            text={`Tables are limited to ${maxRegistrationTableRows} entities. Overflowing sequences will be added to sequential tables.`}
          >
            <BenchlingIcon icon="warning" color={Colors.ORANGE} className="u-tiny-padding-left" />
          </Tooltip>
        )}
      </div>
      <EntryTableDropdownWithCreation
        entryApiId={documentLikeId}
        onTableSelect={onTableSelect}
        onSelectCreateNewTable={onSelectCreateNewTable}
        onClearTableName={onClearTableName}
        selectedTable={selectedTable}
        tableKind={StructuredTableKind.REGISTRATION}
        schemaId={selectedSchemaId || undefined}
        disabled={disabled}
        onEnter={blurOnEnter}
      />
    </div>
  );
}

// WorklistSearchInputWithCreation has a useEffect that depends on the supported types. Define the array here
// so that it is stable between renders.
const WORKLIST_TYPES = [WorklistType.BIOENTITY];

const EXISTING_WORKLIST_WARNING_THRESHOLD = 200;

const ENTITY_TYPE_TO_OUTPUT_COUNT_KEY: Record<
  'construct' | 'fragment' | 'primer',
  'constructOutputCount' | 'totalOutputFragmentCount' | 'primerOutputCount'
> = {
  construct: 'constructOutputCount',
  fragment: 'totalOutputFragmentCount',
  primer: 'primerOutputCount',
};

/**
 * Creates a worklist dropdown section for an output with styling
 */
function WorklistDropdownSection({
  disabled,
  worklistParams,
  entityType,
  dispatch,
}: {
  disabled: boolean;
  worklistParams: WorklistInfo | null;
  entityType: 'construct' | 'primer' | 'fragment';
  dispatch: React.Dispatch<PerformAssemblyModalAction>;
}): JSX.Element {
  return (
    <div className={css(styles.dropdownSection)}>
      <WorklistSearchInputWithCreation
        worklistTypes={WORKLIST_TYPES}
        selectedWorklist={worklistParams}
        onWorklistSelect={(worklistInfo: WorklistInfo): void => {
          dispatch({
            type: 'SET_SELECTED_WORKLIST_ID',
            entityType,
            worklistId: worklistInfo?.targetWorklistId || null,
          });
        }}
        onSelectCreateNewWorklist={(name: string): void => {
          dispatch({
            type: 'SET_NEW_WORKLIST_NAME',
            entityType,
            worklistName: name,
          });
        }}
        onClearWorklistName={(): void => {
          dispatch({
            type: 'SET_SELECTED_WORKLIST_ID',
            entityType,
            worklistId: null,
          });
        }}
        disabled={disabled}
        label="Worklist"
        onEnter={blurOnEnter}
      />
    </div>
  );
}

export interface PerformAssemblyReviewAndFinalizePaneProps {
  state: PerformAssemblyModalState;
  orderedBinDetails: Array<BinDetails>;
  dispatch: React.Dispatch<PerformAssemblyModalAction>;
  fragmentIcon: BenchlingIconName;
  constructOutputCount: number;
  primerOutputCount: number;
  totalOutputFragmentCount: number;
}

export default function PerformAssemblyReviewAndFinalizePane(
  props: PerformAssemblyReviewAndFinalizePaneProps
): JSX.Element {
  const {selectedEntryId, selectedConstructTable, selectedPrimerTable, selectedFragmentTable} = props.state;
  const allowEntrySelection =
    props.state.constructLocationInfo.tagSchemaId ||
    props.state.primerLocationInfo.tagSchemaId ||
    props.state.fragmentLocationInfo.tagSchemaId;

  const shouldShowOverflowWarning = (['construct', 'fragment', 'primer'] as const).some((entityType) => {
    const worklist = props.state[ENTITY_TYPE_TO_LOCATION_KEY[entityType]].worklistParams;
    const outputCount = props[ENTITY_TYPE_TO_OUTPUT_COUNT_KEY[entityType]];
    return (
      (worklist?.newWorklistInfo && outputCount > BenchlingConfig.WORKLISTS_MAX_ITEMS_PER_WORKLIST) ||
      (worklist?.targetWorklistId && outputCount > EXISTING_WORKLIST_WARNING_THRESHOLD)
    );
  });

  return (
    <div className={css(styles.modalBody)}>
      <div className={css(styles.description)}>
        Set the project folder and study where assembly outputs will be saved. Optionally add outputs to a
        worklist or notebook entry for registration.
      </div>
      <div className={css(styles.sideBySideContainer)}>
        <div className={css(styles.halfWidth)}>
          <FieldLabel label="Project folder" isRequired={true} />
          <FolderSelector
            enableRedesign={true}
            showInventory={true}
            showNotebook={false}
            value={props.state.constructLocationInfo.targetFolderId}
            onChange={(e: {target: {value: string}}) => {
              props.dispatch({type: 'SET_FOLDER_ID', value: e.target.value});
            }}
            unsetOnFetchFail={false}
            buttonSize={ButtonSize.MEDIUM}
          />
        </div>
        <div className={css(styles.halfWidth)}>
          <FieldLabel label="Studies" />
          <StudySearchInput
            onChange={(studies) => {
              props.dispatch({type: 'SET_SELECTED_STUDIES', value: studies});
            }}
            selectedItems={props.state.selectedStudies}
            shouldAllowMultiple={true}
            onKeyDown={blurOnEnter}
          />
        </div>
      </div>
      {BenchlingConfig.MOLBIO_ENABLE_ASSEMBLY_NOTEBOOK_INTEGRATION && (
        <>
          <hr className={css(styles.fullWidthDivider)} />
          <div className={css(styles.switchSection)}>
            <div className={css(styles.sectionTitle)}>Add outputs to a Notebook entry</div>
            <Tooltip
              text={
                allowEntrySelection
                  ? 'Add outputs to a Notebook entry'
                  : 'Select a schema for at least one output in the previous step to send to a Notebook entry'
              }
            >
              <Switch
                checked={props.state.addOutputsToEntry}
                onChange={(e) => {
                  props.dispatch({type: 'SET_ADD_OUTPUTS_TO_ENTRY', value: e.target.checked});
                }}
                size="md"
                disabled={!allowEntrySelection}
              />
            </Tooltip>
          </div>
          <div className={css(styles.subtitle)}>
            Optionally send assembly outputs to registration tables in a Notebook entry
          </div>
          {props.state.addOutputsToEntry && allowEntrySelection && (
            <div>
              <div className={css(styles.entrySelector)}>
                <FieldLabel label="Entry" isRequired={true} />
                <EntrySearchInput
                  onChange={(entries) => {
                    props.dispatch({type: 'SET_SELECTED_ENTRY_ID', value: entries[0]?.id || null});
                  }}
                  selectedEntries={selectedEntryId ? [{id: selectedEntryId, name: ''}] : []}
                  shouldAllowMultiple={false}
                  autoFocus={false}
                  onKeyDown={blurOnEnter}
                  placeholder="Select entry"
                  context={SearchContext.FOR_WRITE_ACTION}
                />
              </div>
              <div
                className={css(styles.outputSectionsContainer)}
                {...getTestProps({
                  component: 'PerformAssemblyReviewAndFinalizePane',
                  element: 'notebook-entry-output-sections',
                })}
              >
                <OutputSection
                  title="Constructs"
                  entityCount={props.constructOutputCount}
                  entityName="constructs"
                  icon={ModelTypeConstants.BULK_ASSEMBLY}
                  extraSwatchStyles={
                    getConstructsSwatchesStyles(props.orderedBinDetails.map(({binColor}) => binColor))
                      .constructsSwatches
                  }
                  disabled={!props.state.constructLocationInfo.tagSchemaId || !selectedEntryId}
                >
                  <SchemaDropdownSection
                    selectedSchemaId={props.state.constructLocationInfo.tagSchemaId}
                    disabled={true}
                  />
                  <EntryTableDropdownSection
                    documentLikeId={selectedEntryId || undefined}
                    selectedTable={selectedConstructTable}
                    selectedSchemaId={props.state.constructLocationInfo.tagSchemaId}
                    disabled={!props.state.constructLocationInfo.tagSchemaId || !selectedEntryId}
                    entityCount={props.constructOutputCount}
                    onTableSelect={(selection) => {
                      props.dispatch({
                        type: 'SET_SELECTED_TABLE',
                        entityType: 'construct',
                        value: selection,
                      });
                    }}
                    onSelectCreateNewTable={(tableName) => {
                      props.dispatch({
                        type: 'SET_SELECTED_TABLE',
                        entityType: 'construct',
                        value: {type: TableSelectionType.NEW, name: tableName},
                      });
                    }}
                    onClearTableName={() => {
                      props.dispatch({
                        type: 'SET_SELECTED_TABLE',
                        entityType: 'construct',
                        value: null,
                      });
                    }}
                  />
                </OutputSection>
                {props.state.savePrimers && props.primerOutputCount > 0 && (
                  <OutputSection
                    title="Primers"
                    entityCount={props.primerOutputCount}
                    entityName="primers"
                    icon={FolderItemIcon.OligoV2}
                    disabled={
                      !props.state.savePrimers ||
                      !props.state.primerLocationInfo.tagSchemaId ||
                      !selectedEntryId
                    }
                  >
                    <SchemaDropdownSection
                      selectedSchemaId={props.state.primerLocationInfo.tagSchemaId}
                      disabled={true}
                    />
                    <EntryTableDropdownSection
                      documentLikeId={selectedEntryId || undefined}
                      selectedTable={selectedPrimerTable}
                      selectedSchemaId={props.state.primerLocationInfo.tagSchemaId}
                      disabled={
                        !props.state.savePrimers ||
                        !props.state.primerLocationInfo.tagSchemaId ||
                        !selectedEntryId
                      }
                      entityCount={props.primerOutputCount}
                      onTableSelect={(selection) => {
                        props.dispatch({
                          type: 'SET_SELECTED_TABLE',
                          entityType: 'primer',
                          value: selection,
                        });
                      }}
                      onSelectCreateNewTable={(tableName) => {
                        props.dispatch({
                          type: 'SET_SELECTED_TABLE',
                          entityType: 'primer',
                          value: {type: TableSelectionType.NEW, name: tableName},
                        });
                      }}
                      onClearTableName={() => {
                        props.dispatch({
                          type: 'SET_SELECTED_TABLE',
                          entityType: 'primer',
                          value: null,
                        });
                      }}
                    />
                  </OutputSection>
                )}
                {props.state.saveFragments && props.totalOutputFragmentCount > 0 && (
                  <OutputSection
                    title="Amplified fragments"
                    entityCount={props.totalOutputFragmentCount}
                    entityName="fragments"
                    icon={props.fragmentIcon}
                    disabled={
                      !props.state.saveFragments ||
                      !props.state.fragmentLocationInfo.tagSchemaId ||
                      !selectedEntryId
                    }
                    extraSwatchStyles={
                      getFragmentBinSwatchStyles(
                        props.orderedBinDetails.find(({binId}) => binId === props.state.selectedFragmentBinId)
                          ?.binColor || null
                      ).fragmentBinSwatch
                    }
                  >
                    <SchemaDropdownSection
                      selectedSchemaId={props.state.fragmentLocationInfo.tagSchemaId}
                      disabled={true}
                    />
                    <EntryTableDropdownSection
                      documentLikeId={selectedEntryId || undefined}
                      selectedTable={selectedFragmentTable}
                      selectedSchemaId={props.state.fragmentLocationInfo.tagSchemaId}
                      disabled={
                        !props.state.saveFragments ||
                        !props.state.fragmentLocationInfo.tagSchemaId ||
                        !selectedEntryId
                      }
                      entityCount={props.totalOutputFragmentCount}
                      onTableSelect={(selection) => {
                        props.dispatch({
                          type: 'SET_SELECTED_TABLE',
                          entityType: 'fragment',
                          value: selection,
                        });
                      }}
                      onSelectCreateNewTable={(tableName) => {
                        props.dispatch({
                          type: 'SET_SELECTED_TABLE',
                          entityType: 'fragment',
                          value: {type: TableSelectionType.NEW, name: tableName},
                        });
                      }}
                      onClearTableName={() => {
                        props.dispatch({
                          type: 'SET_SELECTED_TABLE',
                          entityType: 'fragment',
                          value: null,
                        });
                      }}
                    />
                  </OutputSection>
                )}
              </div>
            </div>
          )}
        </>
      )}
      <hr className={css(styles.fullWidthDivider)} />
      <div className={css(styles.switchSection)}>
        <div className={css(styles.sectionTitle)}>Add outputs to a worklist</div>
        <Tooltip text="Add outputs to a worklist">
          <Switch
            checked={props.state.addOutputsToWorklist}
            onChange={(e) => {
              props.dispatch({type: 'SET_ADD_OUTPUTS_TO_WORKLIST', value: e.target.checked});
            }}
            size="md"
          />
        </Tooltip>
      </div>
      <div className={css(styles.subtitle)}>Optionally add assembly outputs to new or existing worklists</div>
      {props.state.addOutputsToWorklist && (
        <>
          {shouldShowOverflowWarning && (
            <div className={css(styles.worklistOverflowWarning)}>
              <InlineBanner
                bannerType={InlineBannerType.WARNING}
                text={`This assembly contains a large number of outputs that may exceed the ${BenchlingConfig.WORKLISTS_MAX_ITEMS_PER_WORKLIST.toLocaleString()}-entity worklist limit. Overflowing entities will be added to additional worklists numbered with the same name.`}
              />
            </div>
          )}
          <div
            className={css(styles.outputSectionsContainer)}
            {...getTestProps({
              component: 'PerformAssemblyReviewAndFinalizePane',
              element: 'worklist-output-sections',
            })}
          >
            <OutputSection
              title="Constructs"
              entityCount={props.constructOutputCount}
              entityName="constructs"
              icon={ModelTypeConstants.BULK_ASSEMBLY}
              extraSwatchStyles={
                getConstructsSwatchesStyles(props.orderedBinDetails.map(({binColor}) => binColor))
                  .constructsSwatches
              }
            >
              <WorklistDropdownSection
                disabled={false}
                worklistParams={props.state.constructLocationInfo.worklistParams}
                entityType="construct"
                dispatch={props.dispatch}
              />
            </OutputSection>
            {props.state.savePrimers && props.primerOutputCount > 0 && (
              <OutputSection
                title="Primers"
                entityCount={props.primerOutputCount}
                entityName="primers"
                icon={FolderItemIcon.OligoV2}
                disabled={!props.state.savePrimers}
              >
                <WorklistDropdownSection
                  disabled={!props.state.savePrimers}
                  worklistParams={props.state.primerLocationInfo.worklistParams}
                  entityType="primer"
                  dispatch={props.dispatch}
                />
              </OutputSection>
            )}
            {props.state.saveFragments && props.totalOutputFragmentCount > 0 && (
              <OutputSection
                title="Amplified fragments"
                entityCount={props.totalOutputFragmentCount}
                entityName="fragments"
                icon={props.fragmentIcon}
                disabled={!props.state.saveFragments}
                extraSwatchStyles={
                  getFragmentBinSwatchStyles(
                    props.orderedBinDetails.find(({binId}) => binId === props.state.selectedFragmentBinId)
                      ?.binColor || null
                  ).fragmentBinSwatch
                }
              >
                <WorklistDropdownSection
                  disabled={!props.state.saveFragments}
                  worklistParams={props.state.fragmentLocationInfo.worklistParams}
                  entityType="fragment"
                  dispatch={props.dispatch}
                />
              </OutputSection>
            )}
          </div>
        </>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  modalBody: {
    padding: '16px',
  },
  description: {
    ...FONT_PROXIMA_14,
    marginBottom: '24px',
  },
  sectionTitle: {
    ...FONT_PROXIMA_14,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },
  fullWidthDivider: {
    border: 0,
    borderTop: `1px solid ${Colors.GRAY_LIGHT}`,
    margin: 0,
    width: '100%',
  },
  outputSectionsContainer: {
    display: 'flex',
    gap: '16px',
    marginBottom: '16px',
    justifyContent: 'flex-start',
  },
  swatch: {
    height: '4px',
    borderRadius: '4px 4px 0px 0px',
    background: Colors.GRAY_DARK,
  },
  outputSection: {
    flex: 1,
    borderColor: Colors.GRAY_LIGHT,
    borderStyle: 'solid',
    borderWidth: '1px',
    borderTop: 'none',
    borderRadius: '4px',
    alignSelf: 'flex-start',
    maxWidth: '230px',
  },
  disabledOutputSection: {
    opacity: 0.6,
  },
  outputSectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  outputSectionTitle: {
    ...FONT_PROXIMA_13,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },
  outputDetail: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  outputSectionContent: {
    padding: '8px 12px',
  },
  countSection: {
    ...FONT_PROXIMA_12,
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
    color: Colors.GRAY_DARKER,
    marginTop: '6px',
  },
  countText: {
    marginLeft: '4px',
  },
  dropdownSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  dropdownTitle: {
    color: Colors.NEAR_BLACK,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },
  switchSection: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '4px',
    marginTop: '16px',
  },
  sideBySideContainer: {
    display: 'flex',
    gap: '16px',
    marginBottom: '16px',
  },
  halfWidth: {
    flex: 1,
  },
  subtitle: {
    ...FONT_PROXIMA_12,
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
    marginBottom: '12px',
  },
  entrySelector: {
    marginBottom: '16px',
    maxWidth: '300px',
  },
  worklistOverflowWarning: {
    marginBottom: '16px',
  },
});
